/**
 * ``useTileDescriptor`` — resolve the basemap tile source the Map view
 * should render, from the deployment config (``GET /api/config`` →
 * ``map.tile_options[]``; ADR-016 amended 2026-06-10).
 *
 * # Why a dedicated map-side hook
 *
 * :func:`useDeploymentConfig` (chrome) owns the ``GET /api/config``
 * TanStack Query. This hook is the thin Map-facing seam over it: it
 * (a) resolves the descriptor only once the config has — while it is
 * pending or errored the result is ``null`` and the Map renders **no**
 * basemap (#1002), never a guess at a network host the deployment's CSP
 * may forbid (which wedged MapLibre's ``load`` for good), and (b) maps the wire
 * :type:`TileSourceDescriptor` to the :type:`TileSourceSpec`
 * :func:`buildMapStyle` / :func:`applyMapTheme` consume, so
 * :file:`style.ts` never imports the chrome/config layer (keeps the
 * map module's dependency graph acyclic).
 *
 * Keeping the query *behind* this seam also lets the Map's wiring tests
 * mock this single hook (matching how every other heavy map sub-hook
 * is stubbed in :file:`Map.test.tsx`) instead of standing up a
 * ``QueryClientProvider`` + a ``/api/config`` MSW handler just to mount
 * ``<Map>``.
 *
 * # Profile-aware selection (REQ-UI-091)
 *
 * The descriptor is resolved from the advertised ``tileOptions`` and the
 * operator's Profile selection (``workingState.map.tile_server``) by
 * :func:`selectTileDescriptor`: the option whose ``id`` matches the
 * Profile wins; an unset/blank/stale id falls back to the advertised
 * ``default: true`` one; a *resolved* but empty option list (older BFF)
 * falls back to the baked OpenFreeMap default. Because the seam reads the **live working
 * state**, switching the Settings basemap picker re-renders the basemap
 * in the same frame (the Map's ``applyMapTheme`` effect depends on the
 * derived ``spec``).
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034 (browser-direct + retained BFF proxy).
 *   - REQ-UI-040 / REQ-UI-045 (paired themes + theme-aware dark layer).
 *   - REQ-UI-091 (Profile-selected basemap).
 *   - ADR-016 amended 2026-06-10; CLAUDE.md §8.1 (fail-safe).
 *   - chrome/use-deployment-config.ts (the underlying query + selector).
 *   - preferences/preferences-context.ts (the live ``map.tile_server``).
 *   - map/style.ts (the consumer of the derived :type:`TileSourceSpec`).
 */
import { useEffect, useMemo } from "react";
import {
  selectTileDescriptor,
  useDeploymentConfig,
  useDeploymentConfigStatus,
  type TileSourceDescriptor,
} from "@/chrome/use-deployment-config";
import { usePreferences } from "@/preferences/preferences-context";
import type { TileSourceSpec } from "@/map/style";

/**
 * Map a wire :type:`TileSourceDescriptor` to the
 * :type:`TileSourceSpec` the style factory consumes.
 *
 * The dark template (REQ-UI-045): when the descriptor names a
 * ``dark_layer``, the dark theme reuses the same ``url_template`` with
 * ``{layer}`` substituted by ``dark_layer`` — so the dark template is
 * the descriptor's ``url_template`` and the factory swaps the layer in.
 * When ``dark_layer`` is ``null`` (NRL Blue Marble — daytime imagery
 * only), the dark template is ``null`` and the Map darkens in-shader.
 *
 * @param descriptor - The selected advertised tile descriptor.
 * @returns The :type:`TileSourceSpec` for :func:`buildMapStyle` /
 *     :func:`applyMapTheme`.
 *
 * Cross-references:
 *   - REQ-UI-045 (theme-aware dark layer); ADR-016 amended 2026-06-10.
 */
export function descriptorToTileSpec(descriptor: TileSourceDescriptor): TileSourceSpec {
  // A non-null ``dark_layer`` means a real dark basemap exists; it
  // shares the descriptor's ``url_template`` (the factory substitutes
  // the dark layer for ``{layer}``). ``null`` → in-shader darkening,
  // signalled by a null dark template.
  const darkUrlTemplate = descriptor.dark_layer != null ? descriptor.url_template : null;
  return {
    // The basemap kind drives the style branch (ADR-016 amended
    // 2026-06-10): ``"vector"`` ⇒ a MapLibre vector source consuming the
    // ``url_template`` as a TileJSON URL + :func:`buildVectorBasemap`;
    // ``"raster"`` (the default) ⇒ the pre-existing raster source/layer.
    type: descriptor.type,
    urlTemplate: descriptor.url_template,
    layer: descriptor.layer,
    format: descriptor.format,
    maxZoom: descriptor.max_zoom,
    darkUrlTemplate,
    darkLayer: descriptor.dark_layer,
    attribution: descriptor.attribution,
  };
}

/** The resolved basemap source the Map view renders. */
export interface ResolvedTileSource {
  /** The selected wire descriptor (the baked default only for a resolved, option-less config). */
  readonly descriptor: TileSourceDescriptor;
  /** The derived spec for :func:`buildMapStyle` / :func:`applyMapTheme`. */
  readonly spec: TileSourceSpec;
}

/**
 * Resolve the basemap tile source for the Map view, honouring the
 * operator's Profile selection (REQ-UI-091).
 *
 * Returns ``null`` while the config is **pending** or **errored** — the
 * Map then renders no basemap (the theme background; markers still paint)
 * and waits for the descriptor to land (#1002). Otherwise the descriptor
 * :func:`selectTileDescriptor` picks from the advertised ``tileOptions`` +
 * the Profile's ``map.tile_server`` id (+ its derived
 * :type:`TileSourceSpec`): the selected option when its id matches, else
 * the advertised ``default: true``, else — for a *resolved* config that
 * advertises no options (older BFF) — the baked OpenFreeMap default
 * (:const:`DEFAULT_TILE_DESCRIPTOR`).
 *
 * Why not a network fallback while unresolved (the pre-#1002 behaviour):
 * on a same-origin PMTiles / org-hosted deployment that host is CSP-blocked,
 * MapLibre 4.x then never marks the source loaded, ``load`` never fires,
 * and the swap effect that would apply the real descriptor never runs —
 * a permanently dead map for any browser that mounted before
 * ``/api/config`` was applied. Fail-safe (CLAUDE.md §8.1) is therefore
 * "no basemap, briefly" — not "a guess, forever".
 *
 * On **error** the seam logs one operator-visible diagnostic (the
 * classification banner already shows UNVERIFIED for the same failure).
 *
 * The dark-layer dimension threads through the derived spec
 * (REQ-UI-045): a descriptor with a ``dark_layer`` drives a real dark
 * basemap on dark theme; a ``null`` ``dark_layer`` keeps the in-shader
 * darkening fallback.
 *
 * @returns ``{ descriptor, spec }`` once resolved; ``null`` while pending
 *     or errored. Memoised on the descriptor identity (Map.tsx feeds the
 *     result into effect deps).
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034 / REQ-UI-035 / REQ-UI-045 / REQ-UI-091.
 *   - ADR-016 amended 2026-06-10 + 2026-08-04; CLAUDE.md §8.1; #1002.
 */
export function useTileDescriptor(): ResolvedTileSource | null {
  const config = useDeploymentConfig();
  const status = useDeploymentConfigStatus();
  const { workingState } = usePreferences();
  useEffect(() => {
    if (status === "error") {
      console.error(
        "[meridian/map/use-tile-descriptor] deployment config unavailable (GET /api/config errored) — rendering no basemap; markers still render. No network fallback is attempted (#1002; REQ-UI-035).",
      );
    }
  }, [status]);
  // The live Profile selection (REQ-UI-091): ``null`` / blank ⇒ deployment
  // default; a stale id falls back to the default inside the selector.
  const selectedId = workingState.map?.tile_server;
  // ``config`` is non-null exactly when the query has data; pending and
  // errored both surface as ``null`` here ⇒ no descriptor.
  const descriptor = config === null ? null : selectTileDescriptor(config.tileOptions, selectedId);
  // Memoise on the descriptor identity so the derived spec is stable
  // across renders that don't change the descriptor — Map.tsx feeds the
  // spec into effect deps + the once-built initial style, so a fresh
  // object each render would be churn.
  const spec = useMemo(
    () => (descriptor === null ? null : descriptorToTileSpec(descriptor)),
    [descriptor],
  );
  return useMemo(
    () => (descriptor === null || spec === null ? null : { descriptor, spec }),
    [descriptor, spec],
  );
}
